import { useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { PortalID } from "frontend/modal/portal-types";
import tracking from "frontend/tracking";
import DropdownPicker from "frontend/ui-components/picker/dropdown-picker";
import { CloseIcon } from "frontend/ui-components/svg-shapes";
import { numberWithCommas } from "frontend/utils/math-utils";
import dynamic from "next/dynamic";
import Skeleton from "react-loading-skeleton";
import consts, { Plan } from "shared/consts";
import BackArrow from "./back-arrow";
import { BillingPeriod } from "./billing-period-picker";
import { CheckoutSource } from "./checkout";
import style from "./payment-page.module.css";
import { addDays } from "shared/util/date-utils";
import classNames from "classnames";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/nextjs";
import { ModalErrorPage } from "frontend/error/error-page";
import { getPathPrefix } from "../utils/getPathPrefix";
import { useBillingHooks } from "./billing-hooks";

const stripePromise = loadStripe(process.env.STRIPE_PKEY as string);

const SELECTED_PLAN = Plan.pro;

const CheckoutForm = dynamic(() => import("./checkout-form"));

export default function PaymentPage({
  seatsCount,
  trialDays,
  priceByPlan,
  source,
  onDismiss,
  showTrial,
}: {
  showTrial: boolean;
  seatsCount: number;
  trialDays: number;
  priceByPlan: Record<Plan, Record<BillingPeriod, number>>;
  source?: CheckoutSource;
  onDismiss?: () => void;
}) {
  const {
    invoiceByPlanCache,
    yearlySaving,
    dropDownOptions,
    seatsMultiplyFactor,
    selectedPeriod,
    showCheckoutForm,
    stripeSecret,
    customerSessionClientSecret,
    loading,
    confirmPayment,
    message,
    seatsToBuy,
    invoicePreviewsAtomValue,
    setSelectedPeriod,
    setShowCheckoutForm,
    setConfirmPayment,
    setMessage,
    setSeatsToBuy,
    onPayButtonClicked,
  } = useBillingHooks({
    seatsCount,
    source,
    type: "page",
  });

  useEffect(() => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "paymet-page-showed", source);
    const checkoutContainer = document.getElementById(PortalID.Checkout);
    if (checkoutContainer) {
      checkoutContainer.style.display = "flex";
    }
    return () => {
      if (checkoutContainer) {
        checkoutContainer.style.display = "none";

        const backgroundModalContainer = document.getElementById(PortalID.Modal);
        if (backgroundModalContainer && backgroundModalContainer.children.length > 0) {
          const backgroundModal = backgroundModalContainer.children[0];
          backgroundModal?.remove();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderOption(interval: BillingPeriod) {
    const pricePerSeat = priceByPlan[Plan.pro][interval];
    const planPrice = pricePerSeat ? `$${numberWithCommas(Number.parseFloat(pricePerSeat.toFixed(2)))}` : "";
    const usersCountText = `${numberWithCommas(seatsToBuy)} ${seatsToBuy > 1 ? "users" : "user"}`;

    const isSelected = selectedPeriod === interval;
    const yearlyText = showTrial ? `${trialDays} days free, then ${planPrice} a month` : `${planPrice} a month`;
    const monthlyText = `${planPrice} a month`;
    const showDiscount = interval === "year";

    const text = showDiscount ? yearlyText : monthlyText;

    return (
      <div key={interval} className={style.optionWrapper}>
        {invoicePreviewsAtomValue ? (
          <>
            <span className={style.planDescription}>{`paid ${interval}ly`}</span>
            <div
              key={interval}
              className={isSelected ? style.selectedOption : style.option}
              onClick={() => setSelectedPeriod(interval)}
            >
              <>
                {showDiscount && (
                  <div className={style.discountLabel}>{`Save ${(100 - yearlySaving).toFixed(0)}%`}</div>
                )}
                <div className={isSelected ? style.selectedOptionButton : style.optionButton}>
                  {isSelected && <div className={style.internalSelectedOptionButton} />}
                </div>
                <div className={style.optionTextContainer}>
                  <span className={style.optionText}>{`${text}`}</span>
                  {false && (
                    <span className={style.optionDetailsText}>{`($${pricePerSeat}/month x ${usersCountText})`}</span>
                  )}
                </div>
              </>
            </div>
          </>
        ) : (
          <>
            <Skeleton width={94} containerClassName={style.planDescription} />
            <Skeleton count={1} width={"546px"} height={"57px"} />
          </>
        )}
      </div>
    );
  }

  function renderPayButton() {
    if (!stripeSecret) {
      return <Skeleton style={{ marginTop: "37px" }} count={1} width={212} height={36} />;
    }

    tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "payment-button-rendered", SELECTED_PLAN);
    return (
      <div className={style.completePurchaseButtonContainer}>
        <button
          onClick={() => {
            if (!confirmPayment) {
              setConfirmPayment(true);
            }
            tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "complete-purchase-clicked", SELECTED_PLAN);
          }}
          className={classNames(style.continueButton, style.completePurchaseButton)}
        >
          {confirmPayment ? "Loading..." : "Complete purchase"}
        </button>
        {message && <span className={style.paymentMessage}>{message}</span>}
      </div>
    );
  }

  function renderPaymentForm() {
    return (
      <Elements
        options={{
          clientSecret: stripeSecret ?? undefined,
          appearance: { theme: "stripe" },
          customerSessionClientSecret: customerSessionClientSecret ?? undefined,
        }}
        stripe={stripePromise}
      >
        <CheckoutForm
          confirmPayment={confirmPayment}
          onLoadingChanged={(isLoading) => {
            if (!isLoading) {
              setConfirmPayment(false);
            }
          }}
          onError={(error) => {
            Sentry.addBreadcrumb({
              message: "Error rendering checkout form (page)",
              category: "error",
              level: "error",
            });
            Sentry.captureException(error, { tags: { type: "billing", page: "checkout-form" } });
            console.error("Stripe error", error);
          }}
          customStyle={{ padding: "40px" }}
          amount={invoiceByPlanCache ? invoiceByPlanCache[selectedPeriod].dueNextPeriod : 0} //track the subscription's non-prorated value
          setMessage={setMessage}
        />
      </Elements>
    );
  }

  function renderCheckoutSkeleton() {
    return (
      <div className={style.checkoutSkeletonContainer}>
        <Skeleton containerClassName={style.checkoutSkeletonContainer} count={4} width={460} height={44} />
      </div>
    );
  }

  function renderCheckout() {
    return stripeSecret ? (
      <div className={style.checkoutContainer}>{renderPaymentForm()}</div>
    ) : (
      renderCheckoutSkeleton()
    );
  }

  function renderSubTotalLine(subTotalTitle: string, subTotalDescription: string, planPrice: string) {
    return (
      <div className={style.subTotalContainer}>
        <div className={style.subTotalDetails}>
          <span className={style.subTotalTitle}>{subTotalTitle}</span>
          <span className={style.optionDetailsText}>{subTotalDescription}</span>
        </div>
        <span className={style.subTotalPrice}>{planPrice}</span>
      </div>
    );
  }

  function renderCancellationNote(formattedDate: string) {
    if (!showTrial) {
      return null;
    }
    return (
      <div className={style.cancellationNoteContainer}>
        <img src={getPathPrefix("/images/info-icon.svg")} alt="info-icon" />
        <span className={style.cancellationNoteText}>{`You won’t be charged until ${formattedDate}.`}</span>
      </div>
    );
  }

  function renderSubTotal() {
    const pricePerSeat = priceByPlan[SELECTED_PLAN][selectedPeriod];
    const usersCountText = `${numberWithCommas(seatsToBuy)} ${seatsToBuy > 1 ? "users" : "user"}`;
    const planInvoicePreview = invoiceByPlanCache && invoiceByPlanCache[selectedPeriod];
    const invoicePreview = planInvoicePreview
      ? selectedPeriod === "year"
        ? planInvoicePreview.dueToday / 12
        : planInvoicePreview.dueToday
      : 0;
    const planPrice = invoicePreview
      ? `$${numberWithCommas(Number.parseFloat((invoicePreview * seatsMultiplyFactor).toFixed(2)))} per month`
      : "";
    const subTotalDescription = `($${pricePerSeat} / month x ${usersCountText})`;

    return (
      <div className={style.subTotalLines}>
        {invoicePreviewsAtomValue ? (
          <>
            {false && selectedPeriod === "year" && renderSubTotalLine("Today's Total", "", "$0")}
            {renderSubTotalLine(`Subtotal`, subTotalDescription, planPrice || "$0")}
          </>
        ) : (
          <Skeleton count={2} width={546} height={24} />
        )}
      </div>
    );
  }

  function renderPlanSelection() {
    const now = new Date();
    const trialEndDate = new Date(addDays(now, trialDays) * 1000);
    const nextPeriodFormatedDate = trialEndDate.toDateString().split(" ").slice(2); //remove the day part of the date
    const newPeriodMonthName = trialEndDate.toLocaleString("default", { month: "long" });
    const formattedTrialEndDate = `${newPeriodMonthName} ${nextPeriodFormatedDate[0]}, ${nextPeriodFormatedDate[1]}`;

    return (
      <>
        <div className={style.optionsWrapper}>
          <div className={style.seatsContainer}>
            <span>How many seats for your team?</span>
            <div className={style.dropDownContainer}>
              <DropdownPicker<number>
                options={dropDownOptions}
                titles={(seatsCount) => `${seatsCount}`}
                optionRenderer={(seatsCount) => {
                  return { element: <span>{`${seatsCount.option} seats`}</span>, enabled: true };
                }}
                onChange={(selectedSeats) => setSeatsToBuy(selectedSeats)}
                isSelected={(v) => v === seatsToBuy}
                enabled={true}
                scrollToSelected={true}
                pickerCustomIcon={getPathPrefix("/images/chevron-black.svg")}
                pickerCustomStyle={{
                  borderRadius: "6px",
                }}
                optionsPickerStyle={{ fontSize: 16, color: "#113357", borderRadius: "6px" }}
              />
            </div>
          </div>
          <div className={style.plansSelectionContainer}>
            <div className={style.options}>
              {["month", "year"].map((interval) => {
                return renderOption(interval as "year" | "month");
              })}
            </div>
            {invoicePreviewsAtomValue ? (
              selectedPeriod === "year" && renderCancellationNote(formattedTrialEndDate)
            ) : (
              <Skeleton width={546} height={44} />
            )}
          </div>
          {false && (
            <div className={style.secureCheckout}>
              <img src={getPathPrefix("/images/checkout/lock.svg")} alt="lock" />
              <span>Secure checkout, cancel anytime</span>
            </div>
          )}
        </div>
        <div className={style.footer}>{renderSubTotal()}</div>
      </>
    );
  }

  function renderCtaButton() {
    return (
      <div className={style.ctaButton}>
        {invoicePreviewsAtomValue ? (
          <button onClick={onPayButtonClicked} className={style.continueButton}>
            {`${loading ? "Loading..." : "Continue"}`}
          </button>
        ) : (
          <Skeleton width={212} height={36} />
        )}
      </div>
    );
  }

  function renderTitle() {
    return <span className={style.title}>Upgrade To WorkCanvas Pro</span>;
  }

  function renderMainLayout() {
    const rightSectionText =
      source?.name === "templates"
        ? `Unlock ${source.value} and 100+ premium templates`
        : "Unlock Pro with 100+ premium templates";
    return (
      <div className={style.container} data-testid="checkout-container">
        <div className={style.content}>
          <div className={style.leftSection}>
            <ErrorBoundary
              fallback={<ModalErrorPage />}
              onError={(error, info) => {
                tracking.trackEvent(
                  consts.TRACKING_CATEGORY.BILLING,
                  "payment-page-error",
                  error.message,
                  source?.value
                );
                console.error("Payment error", error, info);
                Sentry.addBreadcrumb({
                  message: "Error rendering checkout form (page)",
                  category: "error",
                  level: "error",
                  data: { info, source },
                });
                Sentry.captureException(error, { tags: { type: "billing", page: "payment-page" } });
              }}
            >
              {showCheckoutForm ? (
                <BackArrow
                  onClick={() => {
                    setShowCheckoutForm(false);
                    setMessage(null);
                  }}
                  customStyle={{ position: "absolute", top: "10px", left: "16px" }}
                />
              ) : (
                <CloseIcon
                  onClick={onDismiss}
                  style={{ position: "absolute", top: "27px", left: "27px", cursor: "pointer" }}
                />
              )}
              {showCheckoutForm ? (
                <div className={classNames(style.checkoutContent)}>
                  {renderTitle()}
                  {renderCheckout()}
                </div>
              ) : (
                <div className={style.choosePlanContent}>
                  {renderTitle()}
                  {renderPlanSelection()}
                </div>
              )}
              {showCheckoutForm ? renderPayButton() : renderCtaButton()}
            </ErrorBoundary>
          </div>
          <div className={style.rightSection}>
            <div className={style.rightSectionContent}>
              <span className={style.rightSectionTitle}>{rightSectionText}</span>
              <img
                className={style.image}
                src={getPathPrefix("/images/checkout/checkout-templates.svg")}
                alt="checkout-templates"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return renderMainLayout();
}
